// src/utils/xmlParser.js
import { arredondar } from "./mathUtils";

/**
 * Detecta de forma inteligente o fator de conversão de embalagem para unidades físicas de venda.
 * Exemplos:
 * - qCom: 25, qTrib: 300, uTrib: 'UN' -> fator: 12
 * - uCom: 'cx012' -> fator: 12
 * - uCom: 'cx002' -> fator: 2
 * - uCom: 'DZ' ou 'DUZIA' -> fator: 12
 * - xProd: 'CERV HEINEKEN ... SLEEKDES12UNPB' -> fator: 12
 * - xProd: 'AGUA MINERAL 500ML FARDO 12 UND' -> fator: 12
 * - xProd: 'GUARANA ... PACK C/2' -> fator: 2
 * - xProd: 'LATA 350ML SH C/12' -> fator: 12
 */
export const detectarFatorConversao = (uCom = "", qCom = 1, uTrib = "", qTrib = 1, xProd = "") => {
  const uComUpper = (uCom || "").trim().toUpperCase();
  const uTribUpper = (uTrib || "").trim().toUpperCase();
  const nomeUpper = (xProd || "").trim().toUpperCase();

  // 1. Razão Tributável vs Comercial (SEFAZ NT 2016.002 padrão fiscal de bebidas)
  // Ex: qTrib = 300 UN, qCom = 25 EB -> 300 / 25 = 12
  if (
    (uTribUpper === "UN" || uTribUpper === "UND" || uTribUpper === "UNID") &&
    qTrib > 0 &&
    qCom > 0 &&
    qTrib > qCom
  ) {
    const ratio = Math.round(qTrib / qCom);
    if (ratio > 1 && ratio <= 1000) {
      return ratio;
    }
  }

  // 2. Unidade Comercial ou Tributável contendo quantidade embutida (ex: cx012, cx002, cx006, cx024)
  const matchCx = uComUpper.match(/^CX0*(\d+)$/) || uTribUpper.match(/^CX0*(\d+)$/);
  if (matchCx) {
    const val = parseInt(matchCx[1], 10);
    if (val > 1 && val <= 1000) return val;
  }

  // 3. Sigla clássica de Dúzia (DZ / DUZIA)
  if (uComUpper === "DZ" || uComUpper === "DUZIA" || uComUpper === "DUZIAS") {
    return 12;
  }

  // 4. Detecção por Regex na descrição do produto (<xProd>)
  // Ex: "SLEEKDES12UNPB", "FARDO 12 UND", "SH C/12", "PACK C/2", "CX C/24", "C/ 12", "CX 12"
  const regexPatterns = [
    /(?:FARDO|SHRINK|SH|PACK|CX|CAIXA|PCT|PACOTE)\s*(?:C\/|COM|:)?\s*0*(\d+)\s*(?:UN|UND|UNID|LATA|LTS|GF|GARRAFA)?\b/i,
    /0*(\d+)\s*(?:UN|UND|UNID|UNPB|PBR)\b/i,
    /(?:C\/|COM)\s*0*(\d+)\b/i,
  ];

  for (const regex of regexPatterns) {
    const match = nomeUpper.match(regex);
    if (match) {
      const val = parseInt(match[1], 10);
      if (val >= 2 && val <= 100) {
        return val;
      }
    }
  }

  // Padrão: 1 unidade física por unidade comercial
  return 1;
};

/**
 * Lê uma string XML nativa do navegador (DOMParser) e extrai os produtos da NFe ou NFCe.
 *
 * @param {string} xmlString O conteúdo XML importado via FileReader.
 * @returns {object} Objeto contendo o tipo da nota (tpNF: 0 para entrada, 1 para saída) e o array de produtos.
 */
export const parseNotaFiscalXML = (xmlString) => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, "text/xml");

    // Verifica se houve erro no parser nativo
    const parseError = xmlDoc.getElementsByTagName("parsererror");
    if (parseError.length > 0) {
      throw new Error("Erro de formatação no arquivo XML.");
    }

    // 1. Extrair Tipo da Nota (tpNF)
    // Na NFe: <ide><tpNF>0</tpNF></ide> (0=Entrada, 1=Saída)
    const ideNode = xmlDoc.getElementsByTagName("ide")[0];
    if (!ideNode) {
      throw new Error("Arquivo não parece ser uma NFe válida (falta tag <ide>).");
    }

    let tpNF = 0; // Padrão: Entrada
    const tpNFNode = ideNode.getElementsByTagName("tpNF")[0];
    if (tpNFNode && tpNFNode.textContent) {
      tpNF = parseInt(tpNFNode.textContent, 10);
    }

    // Extrair Data de Emissão (dhEmi)
    const dhEmiNode = ideNode.getElementsByTagName("dhEmi")[0];
    const dhEmi = dhEmiNode ? dhEmiNode.textContent : new Date().toISOString();

    // Extrair Valor Total da NF (vNF)
    const totalNode = xmlDoc.getElementsByTagName("total")[0];
    let vNF = 0;
    if (totalNode) {
        const icmsTotNode = totalNode.getElementsByTagName("ICMSTot")[0];
        if (icmsTotNode) {
            const vNFNode = icmsTotNode.getElementsByTagName("vNF")[0];
            if (vNFNode && vNFNode.textContent) {
                vNF = arredondar(parseFloat(vNFNode.textContent));
            }
        }
    }

    // Extrair CNPJ do Emitente (<emit><CNPJ>)
    let cnpjEmitente = "";
    const emitNode = xmlDoc.getElementsByTagName("emit")[0];
    if (emitNode) {
        const cnpjNode = emitNode.getElementsByTagName("CNPJ")[0];
        if (cnpjNode && cnpjNode.textContent) {
            cnpjEmitente = cnpjNode.textContent;
        }
    }

    // Extrair CNPJ/CPF do Destinatário (<dest><CNPJ> ou <dest><CPF>)
    let cnpjDestinatario = "";
    const destNode = xmlDoc.getElementsByTagName("dest")[0];
    if (destNode) {
        const destDocNode = destNode.getElementsByTagName("CNPJ")[0] || destNode.getElementsByTagName("CPF")[0];
        if (destDocNode && destDocNode.textContent) {
            cnpjDestinatario = destDocNode.textContent;
        }
    }

    // 2. Extrair Lista de Produtos (<det>)
    const detNodes = xmlDoc.getElementsByTagName("det");
    const produtos = [];

    for (let i = 0; i < detNodes.length; i++) {
      const prodNode = detNodes[i].getElementsByTagName("prod")[0];
      if (!prodNode) continue;

      // Código do Produto (<cProd>)
      const cProdNode = prodNode.getElementsByTagName("cProd")[0];
      const codigo = cProdNode ? cProdNode.textContent : `ITEM_${i}`;

      // Nome do Produto (<xProd>)
      const xProdNode = prodNode.getElementsByTagName("xProd")[0];
      const nome = xProdNode ? xProdNode.textContent : "Produto sem Nome";

      // Unidade Comercial (<uCom>)
      const uComNode = prodNode.getElementsByTagName("uCom")[0];
      const uCom = uComNode ? uComNode.textContent.trim() : "UN";

      // Quantidade Comercial (<qCom>)
      const qComNode = prodNode.getElementsByTagName("qCom")[0];
      const qCom = qComNode ? arredondar(parseFloat(qComNode.textContent)) : 0;
      const quantidade = qCom; // Mantém retrocompatibilidade

      // Valor Unitário Comercial (<vUnCom>)
      const vUnComNode = prodNode.getElementsByTagName("vUnCom")[0];
      const vUnCom = vUnComNode ? parseFloat(vUnComNode.textContent) : 0;

      // Unidade Tributável (<uTrib>) e Quantidade Tributável (<qTrib>)
      const uTribNode = prodNode.getElementsByTagName("uTrib")[0];
      const uTrib = uTribNode ? uTribNode.textContent.trim() : "";

      const qTribNode = prodNode.getElementsByTagName("qTrib")[0];
      const qTrib = qTribNode ? arredondar(parseFloat(qTribNode.textContent)) : 0;

      const vUnTribNode = prodNode.getElementsByTagName("vUnTrib")[0];
      const vUnTrib = vUnTribNode ? parseFloat(vUnTribNode.textContent) : 0;

      // Detecção inteligente do fator de conversão de embalagem/dúzia para unidades
      const fatorConversaoSugerido = detectarFatorConversao(uCom, qCom, uTrib, qTrib, nome);

      // Código de Barras EAN (opcional)
      const cEANNode = prodNode.getElementsByTagName("cEAN")[0];
      const ean = cEANNode && cEANNode.textContent !== "SEM GTIN" ? cEANNode.textContent : "";

      // NCM (opcional, fallback para 'N/A')
      const ncmNode = prodNode.getElementsByTagName("NCM")[0];
      const ncm = ncmNode && ncmNode.textContent ? ncmNode.textContent : "N/A";

      // Valor do Produto (vProd)
      const vProdNode = prodNode.getElementsByTagName("vProd")[0];
      const vProd = vProdNode ? arredondar(parseFloat(vProdNode.textContent)) : 0;

      // Código Fiscal de Operações e Prestações (CFOP)
      const cfopNode = prodNode.getElementsByTagName("CFOP")[0];
      const cfop = cfopNode && cfopNode.textContent ? cfopNode.textContent : "";

      produtos.push({
        codigo,
        nome,
        quantidade,
        uCom,
        qCom,
        vUnCom,
        uTrib,
        qTrib,
        vUnTrib,
        fatorConversaoSugerido,
        ean,
        ncm,
        vProd,
        cfop
      });
    }

    // Extrair Chave de Acesso (chNFe)
    // Tenta pegar da tag chNFe primeiro (que fica dentro do protNFe).
    // Se não achar, tenta pegar do atributo Id da tag infNFe (removendo o "NFe" do começo).
    let chaveAcesso = "";
    const chNFeNode = xmlDoc.getElementsByTagName("chNFe")[0];
    if (chNFeNode && chNFeNode.textContent) {
      chaveAcesso = chNFeNode.textContent;
    } else {
      const infNFeNode = xmlDoc.getElementsByTagName("infNFe")[0];
      if (infNFeNode && infNFeNode.getAttribute("Id")) {
        chaveAcesso = infNFeNode.getAttribute("Id").replace("NFe", "");
      }
    }

    if (!chaveAcesso) {
      throw new Error("Não foi possível encontrar a Chave de Acesso na Nota Fiscal.");
    }

    return {
      success: true,
      tpNF, // 0: Entrada, 1: Saída
      dhEmi,
      vNF,
      chaveAcesso, // Nova prop extraída
      cnpjEmitente, // Utilizado para o mapeamento e validação
      cnpjDestinatario, // Novo campo extraído
      produtos
    };

  } catch (error) {
    console.error("Erro ao fazer parse do XML:", error);
    return {
      success: false,
      error: error.message || "Não foi possível ler o arquivo de Nota Fiscal."
    };
  }
};
